import {
  State,
  TransformsPlugin,
  RenderPlugin,
  TweenPlugin,
  minimalLight,
  ChildOf,
  Pair,
} from '@multiplekex/shallot';
import { OrbitPlugin, LinesPlugin, ArrowsPlugin, TextPlugin } from '@multiplekex/shallot/extras';
import { Text } from '@multiplekex/shallot/extras';
import {
  createStepPlugin,
  setupCanvas,
  setupScrollPassthrough,
  setupDemo,
  TokenIndex,
} from '@blog-components/sequence';
import { SpecdecPlugin, BorderPlugin, ProbabilityDistributionPlugin, ProbabilityDistribution, BarHighlight, setDistributionProbabilities, GradientLegendPlugin, TokenReplacement, setTokenReplacementTexts } from '../src';

const GROUP_INPUT = 0;
const GROUP_OUTPUT = 1;
const GROUP_MARK = 2;

const STEPS = [
  { title: 'Draft Model', description: 'The <span class="term-draft-model">draft model</span> generates output tokens.' },
  { title: 'Target Model', description: 'The <span class="term-target-model">target model</span> processes all outputs in a single forward pass.' },
  { title: 'Target Distribution', description: 'The <span class="term-target-model">target model</span> produces a probability distribution, <span class="term-target-model">p<sub>target</sub></span>, at each position.' },
  { title: 'Draft Distribution', description: 'Each token was sampled from <span class="term-draft-model">p<sub>draft</sub></span>. We compare this to <span class="term-target-model">p<sub>target</sub></span>.' },
  { title: 'Acceptance', description: 'We use <code>min(1, <span class="term-target-model">p<sub>target</sub></span> / <span class="term-draft-model">p<sub>draft</sub></span>)</code> to decide whether to accept each token.' },
  { title: 'Bees', description: 'We accept "bees". <span class="term-target-model">p<sub>target</sub></span> &gt; <span class="term-draft-model">p<sub>draft</sub></span>, so the formula gives <span class="term-accept">1</span>, or <span class="term-accept">100%</span> chance to accept.' },
  { title: 'Communicate', description: '"communicate" also has <span class="term-target-model">p<sub>target</sub></span> &gt; <span class="term-draft-model">p<sub>draft</sub></span>, so we accept.' },
  { title: 'Using', description: 'For "using", <span class="term-target-model">p<sub>target</sub></span> &lt; <span class="term-draft-model">p<sub>draft</sub></span>. The formula gives <span class="term-accept">0.8</span>, or <span class="term-accept">80%</span> chance. We accept.' },
  { title: 'A', description: '"a" has <span class="term-target-model">p<sub>target</sub></span> slightly below <span class="term-draft-model">p<sub>draft</sub></span>. <span class="term-accept">90%</span> chance. We accept.' },
  { title: 'Buzz', description: 'For "buzz", <span class="term-target-model">p<sub>target</sub></span> is much lower than <span class="term-draft-model">p<sub>draft</sub></span>. The formula gives <span class="term-reject">0.2</span>, or <span class="term-reject">20%</span> chance. This time, we reject.' },
  { title: 'Residual', description: 'After rejection, we resample from <span class="term-residual">p<sub>residual</sub></span> = <code>max(0, <span class="term-target-model">p<sub>target</sub></span> - <span class="term-draft-model">p<sub>draft</sub></span>)</code>.' },
  { title: 'Resampling', description: 'Finally, we replace "buzz" with the resampled token: "wiggle".' },
  { title: 'Repeat', description: 'We repeat the same process, continuing generation from "wiggle".' },
];

async function init(): Promise<void> {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  if (!canvas) return;

  setupCanvas(canvas);
  setupScrollPassthrough(canvas);
  window.addEventListener('resize', () => setupCanvas(canvas));

  const state = await State.new()
    .withCanvas(canvas)
    .withLoading(minimalLight(canvas))
    .withPlugins(
      TransformsPlugin,
      RenderPlugin,
      OrbitPlugin,
      LinesPlugin,
      ArrowsPlugin,
      TextPlugin,
      TweenPlugin,
      createStepPlugin(),
      SpecdecPlugin,
      BorderPlugin,
      ProbabilityDistributionPlugin,
      GradientLegendPlugin
    )
    .withScene('../scenes/step-by-step.scene')
    .build();

  const inputTokens = ['The', 'scientist', 'discovered', 'that'];
  const outputTokens = ['bees', 'communicate', 'using', 'a', 'buzz', 'sound'];

  for (const eid of state.query([TokenIndex, Text])) {
    const idx = TokenIndex.index[eid];
    const group = TokenIndex.group[eid];

    if (group === GROUP_INPUT && idx < inputTokens.length) {
      Text.content[eid] = inputTokens[idx];
    } else if (group === GROUP_OUTPUT && idx < outputTokens.length) {
      Text.content[eid] = outputTokens[idx];
    }
  }

  const BAR_COUNT = 15;

  const tokenData = [
    {
      label: 'bees',
      barIndex: 6,
      yOffset: 1.2,
      targetProbs: [0.08, 0.15, 0.04, 0.22, 0.06, 0.12, 0.75, 0.18, 0.03, 0.09, 0.25, 0.05, 0.11, 0.07, 0.02],
      draftProbs:  [0.12, 0.08, 0.06, 0.18, 0.10, 0.15, 0.55, 0.25, 0.05, 0.14, 0.20, 0.08, 0.09, 0.04, 0.03],
    },
    {
      label: 'communicate',
      barIndex: 9,
      yOffset: 2.3,
      targetProbs: [0.05, 0.12, 0.08, 0.15, 0.04, 0.18, 0.06, 0.10, 0.03, 0.70, 0.22, 0.07, 0.14, 0.09, 0.02],
      draftProbs:  [0.08, 0.10, 0.06, 0.12, 0.05, 0.15, 0.09, 0.14, 0.04, 0.50, 0.18, 0.06, 0.11, 0.07, 0.03],
    },
    {
      label: 'using',
      barIndex: 7,
      yOffset: 1.0,
      targetProbs: [0.12, 0.18, 0.10, 0.15, 0.08, 0.20, 0.14, 0.48, 0.11, 0.16, 0.09, 0.13, 0.07, 0.10, 0.05],
      draftProbs:  [0.10, 0.15, 0.12, 0.18, 0.09, 0.17, 0.11, 0.60, 0.13, 0.14, 0.08, 0.12, 0.09, 0.11, 0.06],
    },
    {
      label: 'a',
      barIndex: 5,
      yOffset: 1.1,
      targetProbs: [0.08, 0.12, 0.15, 0.10, 0.18, 0.54, 0.14, 0.09, 0.20, 0.11, 0.07, 0.16, 0.06, 0.13, 0.04],
      draftProbs:  [0.10, 0.14, 0.12, 0.08, 0.16, 0.60, 0.11, 0.07, 0.18, 0.13, 0.09, 0.15, 0.05, 0.10, 0.06],
    },
    {
      label: 'buzz',
      barIndex: 11,
      yOffset: 1.3,
      targetProbs: [0.15, 0.08, 0.12, 0.65, 0.06, 0.18, 0.14, 0.20, 0.09, 0.16, 0.07, 0.10, 0.11, 0.08, 0.05],
      draftProbs:  [0.08, 0.12, 0.10, 0.08, 0.09, 0.14, 0.11, 0.18, 0.13, 0.16, 0.07, 0.50, 0.10, 0.08, 0.06],
      wiggleBarIndex: 3,
      wiggleYOffset: 4.5,
    },
  ];

  const probBarsEids = [...state.query([ProbabilityDistribution])];
  const probBarsEid = probBarsEids[0];

  const highlightEids = [...state.query([BarHighlight])];
  const highlight0Eid = highlightEids[0];
  const highlight1Eid = highlightEids[1];

  let highlight0LabelEid: number | undefined;
  let highlight1LabelEid: number | undefined;

  if (highlight0Eid !== undefined) {
    for (const childEid of state.query([Pair(ChildOf.relation, highlight0Eid), Text])) {
      highlight0LabelEid = childEid;
      break;
    }
  }
  if (highlight1Eid !== undefined) {
    for (const childEid of state.query([Pair(ChildOf.relation, highlight1Eid), Text])) {
      highlight1LabelEid = childEid;
      break;
    }
  }

  if (highlight0LabelEid !== undefined) {
    Text.content[highlight0LabelEid] = tokenData[0].label;
  }
  if (highlight1LabelEid !== undefined) {
    Text.content[highlight1LabelEid] = 'wiggle';
  }

  if (probBarsEid !== undefined) {
    ProbabilityDistribution.barCount[probBarsEid] = BAR_COUNT;
    setDistributionProbabilities(probBarsEid, tokenData[0].targetProbs, tokenData[0].draftProbs);
  }

  let output4Eid: number | undefined;
  let mark4Eid: number | undefined;

  for (const eid of state.query([TokenIndex, Text])) {
    const idx = TokenIndex.index[eid];
    const group = TokenIndex.group[eid];

    if (group === GROUP_OUTPUT && idx === 4) {
      output4Eid = eid;
      state.addComponent(eid, TokenReplacement);
      setTokenReplacementTexts(eid, 'buzz', 'wiggle');
    } else if (group === GROUP_MARK && idx === 4) {
      mark4Eid = eid;
      state.addComponent(eid, TokenReplacement);
      setTokenReplacementTexts(eid, '✗', '✓');
    }
  }

  const updateGraphForStep = (step: number) => {
    if (probBarsEid === undefined) return;

    const tokenIndex = Math.max(0, step - 5);
    if (tokenIndex < tokenData.length) {
      const data = tokenData[tokenIndex];
      setDistributionProbabilities(probBarsEid, data.targetProbs, data.draftProbs);

      if (highlight0Eid !== undefined) {
        BarHighlight.barIndex[highlight0Eid] = data.barIndex;
        BarHighlight.yOffset[highlight0Eid] = data.yOffset;
      }
      if (highlight0LabelEid !== undefined) {
        Text.content[highlight0LabelEid] = data.label;
      }
    }
  };

  setupDemo(canvas, state, STEPS, updateGraphForStep);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
